import { useState } from 'react'

import type { Session } from '@ctcells/contract'
import { SESSIONS } from '@ctcells/fixtures'
import { t } from '@ctcells/i18n'

import { Badge } from '../../atoms/Badge.tsx'
import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { SettingsRow, SettingsSection } from '../../layout/SettingsList.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { type ScreenAccess } from '../access.ts'
import { dateTimeText, orDash, timeText } from '../format.ts'
import { OfflineNotice } from '../resourceParts.tsx'

import { PasswordChangeModal } from './PasswordChangeModal.tsx'
import { PreferenceSettings } from './PreferenceSettings.tsx'
import { groupSessionsByDevice, type DeviceSessions } from './sessionDevices.ts'

import '../screens.css'
import '../resource.css'

/**
 * 내 계정 — 와이어프레임 `profile()` 2756행. 왼쪽 비밀번호 · 오른쪽 세션(eric 지시 2026-09-16).
 *
 * 🔴 **세션 구역은 살아 있다.** 관리 › 세션 탭은 없어졌지만 이건 뺐다가 되살린 것이다(§18-3).
 * 🔴 **비밀번호 입력 규칙을 상시 표시하지 않는다.** 위반했을 때만 그 자리에 나타난다(§1-1) —
 *    `PasswordField` 가 그 규약을 쥐고 있다.
 * 🔴 「이 기기」 세션은 스스로 끊을 수 없다. 비활성 + 사유다.
 * 🔴 **개인 설정(테마 · 언어)이 여기 산다**(eric 지시 2026-09-17). 비밀번호 · 세션 배치는
 *    건드리지 않고 아래에 구역 하나를 덧댔다 — 왼쪽 비밀번호 · 오른쪽 세션은 eric 이 정한 것이다.
 *
 * ⚠ **어느 세션이 「이 기기」인지 서버가 알려줘야 한다.** 계약 `Session` 에 그 표시가 없다 —
 *   지금은 `currentSessionId` 를 호출부가 넘기는 자리만 뚫어 뒀다. 팀장 보고 대상.
 */
export interface AccountScreenProps {
  sessions?: readonly Session[]
  /**
   * 세션 목록을 **못 봤을 때의 사유**. 넘어오면 세션 표의 빈 문구가 이것이 된다.
   *
   * 🔴 **`GET /sessions` 는 admin 전용이다**(`views/sessions.py` `ALLOWED_ROLES`) —
   *    그런데 「내 계정」은 **역할 무관 자기 화면**이고 비밀번호 변경은
   *    `PUT /users/{id}/password` 로 **전 역할 self-service** 다. 종전에는 이 403 하나가
   *    화면 전체를 덮어서 **operator·viewer 가 자기 비밀번호를 못 바꿨다**(2026-09-21).
   *    ⇒ 세션 표는 부가 자료다. 접히는 것은 **그 구역뿐**이다.
   */
  sessionsNotice?: string | undefined
  /** 지금 이 화면이 쓰는 세션. 서버가 알려줘야 하는 값이다 */
  currentSessionId?: string
  access?: ScreenAccess
  /** 「지금」 — 만료된 세션을 거르는 기준. 안 넘기면(demo) 만료 판정을 하지 않는다 */
  now?: number
  /**
   * 비밀번호 변경 — `POST /users/{id}/password`(본인 · 현재 비밀번호 필수). 없으면(demo) 「변경」이
   * 비활성 + 사유다. 종전 「저장」은 핸들러가 없는 죽은 단추였다(2026-09-23).
   */
  onChangePassword?: ((currentPassword: string, nextPassword: string) => void) | undefined
  changingPassword?: boolean
  /** 서버 거절 사유. 창 안에 선다 */
  changePasswordError?: string | undefined
  /** 마지막 변경 성공 시각(ISO). 값이 바뀌면 창이 닫히고 줄에 「변경됨 HH:MM」이 선다 */
  passwordChangedAt?: string | undefined
  /** 창을 열 때 이전 거절 사유를 지운다 */
  onResetPasswordError?: (() => void) | undefined
}

/** 로그인 정보 표에 보이는 기기 수 상한 */
const LOGIN_INFO_MAX_DEVICES = 10

export function AccountScreen({
  sessions = SESSIONS,
  sessionsNotice,
  currentSessionId = SESSIONS[0]?.session_id,
  access = {},
  now,
  onChangePassword,
  changingPassword = false,
  changePasswordError,
  passwordChangedAt,
  onResetPasswordError,
}: AccountScreenProps) {
  /*
   * 비밀번호 창. 🔴 **성공은 서버 응답으로만 안다** — `passwordChangedAt` 이 열었을 때의 값과 달라지면
   *    닫는다(낙관적으로 먼저 닫지 않는다). 거절이면 창이 남아 사유를 보인다.
   */
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [openedAt, setOpenedAt] = useState<string | undefined>(undefined)
  if (passwordOpen && passwordChangedAt !== undefined && passwordChangedAt !== openedAt) {
    setPasswordOpen(false)
  }
  const passwordGate =
    onChangePassword === undefined
      ? ({ disabled: true, disabledReason: t('reason.demoSource') } as const)
      : ({ disabled: false } as const)

  /*
   * 🔴 **한 줄 = 기기**(eric 2026-09-22 「기기별로 나와야 하는 거 아니야?」). 세션은 로그인마다 한 줄씩 생겨
   *    한 계정에 26줄이 쌓였다. 서버가 기기를 가르는 축은 `client` 하나라 그것으로 묶는다(`sessionDevices.ts`).
   */
  /* 최근 본 순으로 **기기 열 대까지**만(eric 2026-09-22 「로그인 정보만 적어줘, 최대 10대까지만」). 「이 기기」가 맨 위다 */
  const devices = groupSessionsByDevice(sessions, currentSessionId, now).slice(
    0,
    LOGIN_INFO_MAX_DEVICES,
  )

  /*
   * 🔴 **로그인 정보 — 읽기만 한다**(eric 2026-09-22 「강제 종료 버튼 빼고 로그인 정보로 바꿔야 할 것 같은데. 다른 세션 모두
   *    종료 버튼도 빼고 — 기기 구분을 못하는데」). 한 줄 = 기기(`client` 표지 · 같은 브라우저 프로필). 열은 **셋**이다 —
   *    기기 · 로그인 · 마지막 활동. 종료 단추는 관리자의 사용자 상세에만 있다(세션 한 건씩 · 감사 기록과 함께).
   * 🔴 **「절대 만료」 열과 「세션 N」 칩은 없다**(eric 2026-09-23 「세션 3 이거 없애고 절대 만료 이거 없애」).
   *    되살리지 마라 — 만료 시각은 사람이 할 일이 없는 값이고, 세션 수는 「기기」 축과 어긋나 보인다.
   *    만료된 세션을 **거르는** 판정(`isLiveSession`)은 그대로다 — 그건 표시가 아니라 필터다.
   * 🔴 **머리글 정렬은 걸되 처음 정렬(`defaultSort`)은 두지 않는다**(2026-09-30 「모든 게시판」). 처음 차례는
   *    「이 기기」 맨 위 → 최근 본 순이다(`groupSessionsByDevice`). 「마지막 활동 내림차순」을 처음 차례로 걸면
   *    이 기기가 맨 위에서 밀릴 수 있다 — 서버는 활동 시각을 간격(슬라이딩 갱신 간격)마다만 쓰기 때문이다.
   *    머리글을 누르면 그때부터 그 열의 차례다(열 대는 최근 본 순으로 먼저 고른 것 안에서만 다시 선다).
   */
  const sessionColumns: Column<DeviceSessions>[] = [
    {
      key: 'client',
      header: t('column.device'),
      sort: 'text',
      sortValue: (group) => group.client,
      /*
       * 🔴 **`orDash` 를 거친다** — `Session.client` 는 서버가 `exclude_none=True` 로 내보내 **키째 빠져서 온다**
       *    (실물 24건 중 8건). 그 세션들은 「—」 한 묶음이다.
       */
      cell: (group) => (
        /* 이름 오른쪽에 「이 기기」 칩이 **같은 줄**에 붙는다(eric 2026-09-22 「지금 한 줄을 다 차지하고 있어」) */
        <b className="devcell">
          <span className="mono">{orDash(group.client === '' ? undefined : group.client)}</span>
          {group.current ? <Badge>{t('value.thisDevice')}</Badge> : null}
        </b>
      ),
    },
    {
      key: 'issued',
      header: t('column.loggedInAt'),
      nowrap: true,
      sort: 'time',
      sortValue: (group) => group.issuedAt,
      cell: (group) => <span className="num">{dateTimeText(group.issuedAt)}</span>,
    },
    {
      key: 'seen',
      header: t('column.lastActive'),
      nowrap: true,
      sort: 'time',
      sortValue: (group) => group.lastSeenAt,
      cell: (group) => <span className="num">{dateTimeText(group.lastSeenAt)}</span>,
    },
  ]

  return (
    <ListPage title={t('screen.account.title')}>
      <OfflineNotice shown={access.offline === true} />
      {/*
        배치 — eric 2026-09-22 「개인 설정을 맨 위로, 그 밑에 비번 바꾸기. 세션은 오른쪽 열에 2열로」.
        왼쪽 열: 개인 설정 구획 하나(테마 · 테마색 · 언어 · **비밀번호**). 오른쪽 열: 세션.
        🔴 비밀번호는 제 구획(제목 「비밀번호」)을 갖지 않고 개인 설정의 마지막 줄로 들어간다
           (eric 2026-09-23 「비밀번호 / 비밀번호 •••• 이거를 따로 두지 말고. 비밀번호를 개인 설정 아래에 추가해줘」).
        고정 2열(`.acctpg`)이고 미디어 쿼리는 없다.
      */}
      <div className="acctpg">
        <div className="acctpg__col">
          <PreferenceSettings>
            {/* 한 줄 + 「변경」 → 창(eric 2026-09-23). 세 칸을 구획에 펼쳐 두던 모양은 걷었다 */}
            <SettingsRow
              action={
                <Button
                  onClick={() => {
                    onResetPasswordError?.()
                    setOpenedAt(passwordChangedAt)
                    setPasswordOpen(true)
                  }}
                  size="pill"
                  {...passwordGate}
                >
                  {t('action.change')}
                </Button>
              }
              label={t('field.password')}
              /*
               * 🔴 **가린 표시(`••••`)를 두지 않는다**(eric 2026-09-23 「비밀번호에서 •••• 이거 없애줘」).
               *    아직 안 바꿨으면 값 칸이 비고 라벨 + 「변경」만 선다 — 다른 설정 줄과 같은 모양이다.
               *    「변경됨 HH:MM」은 남는다. 그건 가린 값이 아니라 **서버 응답으로만 켜지는 결과**다.
               */
              value={
                passwordChangedAt === undefined ? null : (
                  <span className="muted">
                    {t('value.passwordChangedAt', { time: timeText(passwordChangedAt) })}
                  </span>
                )
              }
            />
          </PreferenceSettings>
        </div>
        <SettingsSection title={t('section.loginInfo')}>
          <DataTable
            caption={t('section.loginInfo')}
            columns={sessionColumns}
            density="compact"
            /* 🔴 못 봤으면 「활성 세션 없음」이라고 단언하지 않는다 — 사유를 그대로 적는다 */
            emptyLabel={sessionsNotice ?? t('state.empty.sessions')}
            rowKey={(group) => group.client}
            rows={devices}
          />
        </SettingsSection>
      </div>
      {passwordOpen && onChangePassword !== undefined ? (
        <PasswordChangeModal
          error={changePasswordError}
          onClose={() => setPasswordOpen(false)}
          onSubmit={onChangePassword}
          pending={changingPassword}
        />
      ) : null}
    </ListPage>
  )
}
